import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';

export interface IUserFieldValueSimple {
  campo: string;
  value: string;
}

export type NivelUsuario = 'Latão' | 'Prata' | 'Ouro' | 'Diamante';

export interface IUser {
  id?: number;
  nome: string;
  email: string;
  grupo_id: number;
  empresa_id: number;
  ativo: boolean;
  password?: string;
  is_sympla: boolean;
  confirmPassword?: string;
  grupo?: any;
  imagem_url?: string;
  campos_valores?: IUserFieldValueSimple[];
  nivel?: NivelUsuario;
  data_admissao?: string;
  genero?: string;
  departamento_id?: number | null;
  departamento?: any;
}

export interface IUserStats {
  total: number;
  ativos: number;
  inativos: number;
  por_grupo: { nome: string; quantidade: number }[];
}

interface IUserFilter {
  userId: string;
}

export const useUsers = () => {
  const getUsers = async (key: QueryFunctionContext): Promise<IData<IUser[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<IUser[]>>('/api/v1/usuarios/', {
      params: filters,
    });
    return res.data;
  };

  const getUser = async (key: QueryFunctionContext): Promise<IDataNoPagination<IUser>> => {
    const { userId } = key.queryKey[1] as IUserFilter;
    const res = await api.get<IDataNoPagination<IUser>>(`/api/v1/usuarios/${userId}/`);
    return res.data;
  };

  const createUser = async (data: IUser): Promise<IData<IUser>> => {
    const res = await api.post<IData<IUser>>('/api/v1/usuarios/', data);
    return res.data;
  };

  const updateUser = async (data: IUser): Promise<IData<IUser>> => {
    const res = await api.put<IData<IUser>>(`/api/v1/usuarios/${data.id}/`, data);
    return res.data;
  };

  const deleteUser = async (userId: string) => {
    const res = await api.delete(`/api/v1/usuarios/${userId}/`);
    return res.data;
  };

  const getStats = async (): Promise<IDataNoPagination<IUserStats>> => {
    const res = await api.get<IDataNoPagination<IUserStats>>('/api/v1/usuarios/estatisticas/');
    return res.data;
  };

  const uploadImage = async (userId: number, file: File): Promise<IDataNoPagination<IUser>> => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await api.post<IDataNoPagination<IUser>>(
      `/api/v1/usuarios/${userId}/imagem/`,
      formData,
    );
    return res.data;
  };

  const deleteImage = async (userId: number): Promise<IDataNoPagination<IUser>> => {
    const res = await api.delete<IDataNoPagination<IUser>>(`/api/v1/usuarios/${userId}/imagem/`);
    return res.data;
  };

  const getUserById = async (userId: number): Promise<IDataNoPagination<IUser>> => {
    const res = await api.get<IDataNoPagination<IUser>>(`/api/v1/usuarios/${userId}/`);
    return res.data;
  };

  const updateNivel = async (data: {
    userId: number;
    nivel: NivelUsuario;
  }): Promise<IDataNoPagination<IUser>> => {
    const res = await api.put<IDataNoPagination<IUser>>(`/api/v1/usuarios/${data.userId}/nivel/`, {
      nivel: data.nivel,
    });
    return res.data;
  };

  return {
    getUsers,
    getUser,
    getUserById,
    getStats,
    uploadImage,
    deleteImage,
    updateNivel,

    createUser,
    updateUser,
    deleteUser,
  };
};
